import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useLocation } from 'react-router-dom'

import useMediaQuery from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'
import { RoutePath } from '@/routes'

import { Flex } from '../common/Box'
import { SilkscreenText } from '../common/Text'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../ui/accordion'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card'

const Navs: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  const { gtXL } = useMediaQuery()
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const navs = useMemo(
    () => [
      {
        title: t('nav.home'),
        path: RoutePath.HOME
      },
      {
        title: t('nav.airdrop'),
        path: RoutePath.AIRDROP
      },
      {
        title: t('nav.mining'),
        path: RoutePath.MINING,
        children: [
          {
            title: t('nav.mining.platform'),
            path: RoutePath.MINING_PLATFORM
          },
          {
            title: t('nav.mining.influence'),
            path: RoutePath.MINING_INFLUENCE
          }
        ]
      },
      {
        title: t('nav.dao'),
        path: RoutePath.DAO
      },
      {
        title: t('nav.staking'),
        path: RoutePath.STAKING
      },
      {
        title: t('nav.vote'),
        path: RoutePath.VOTE
      },
      {
        title: t('nav.proposals'),
        path: RoutePath.PROPOSAL
      }
    ],
    [t]
  )

  const renderNavChildren = useCallback(
    (children: (typeof navs)[number]['children'] = []) =>
      children.map((child) => (
        <NavLink
          key={child.path}
          to={child.path}
          className={cn(
            'mx-20 block rounded-sm border-b p-2 last:border-none hover:bg-primary xl:mx-0 xl:border-none',
            pathname === child.path && 'bg-primary'
          )}
          onClick={() => {
            onNavigate?.()
          }}
        >
          {child.title}
        </NavLink>
      )),
    [onNavigate, pathname]
  )

  return (
    <Flex
      className={cn(
        'silkscreen flex-col text-center xl:flex-row xl:items-center xl:justify-center',
        'gap-0 text-base xl:gap-4 xl:text-base'
      )}
    >
      {/* {navs.map((nav) => (
        <NavLink
          key={nav.path}
          to={nav.path}
          className={cn(
            'cursor-pointer border-b text-white xl:border-none',
            'py-4 xl:py-0',
            pathname === nav.path
              ? 'font-bold text-primary underline underline-offset-4'
              : ''
          )}
          onClick={() => {
            onNavigate?.()
          }}
        >
          {nav.title}
        </NavLink>
      ))} */}
      {navs.map((nav) =>
        nav.children ? (
          gtXL ? (
            <HoverCard key={nav.path} openDelay={0}>
              <HoverCardTrigger>
                <SilkscreenText
                  className={cn(
                    'cursor-pointer border-b xl:border-none',
                    pathname.includes(nav.path) &&
                      'text-primary underline underline-offset-4'
                  )}
                >
                  {nav.title}
                </SilkscreenText>
              </HoverCardTrigger>
              <HoverCardContent
                className="grid w-auto border-none bg-secondary p-1"
                sideOffset={10}
              >
                {renderNavChildren(nav.children)}
              </HoverCardContent>
            </HoverCard>
          ) : (
            <Accordion key={nav.path} type="single" collapsible>
              <AccordionItem value={nav.path}>
                <AccordionTrigger
                  className={cn('border-none p-0 !no-underline')}
                >
                  <SilkscreenText
                    className={cn(
                      '-mr-4 w-full cursor-pointer text-center',
                      'py-4 xl:py-0',
                      pathname.includes(nav.path) &&
                        'text-primary underline underline-offset-4'
                    )}
                  >
                    {nav.title}
                  </SilkscreenText>
                </AccordionTrigger>
                <AccordionContent>
                  {renderNavChildren(nav.children)}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )
        ) : (
          <NavLink
            key={nav.path}
            to={nav.path}
            className={cn(
              'cursor-pointer border-b last:border-none xl:border-none',
              'py-4 xl:py-0',
              pathname === nav.path
                ? 'text-primary underline underline-offset-4'
                : ''
            )}
            onClick={() => {
              onNavigate?.()
            }}
          >
            {nav.title}
          </NavLink>
        )
      )}
    </Flex>
  )
}

export default Navs
